import { useEffect, useState } from 'react';

export interface UseProgressDebugReturn {
  debugPNL: number;
  debugError: boolean;
  actualPNL: number;
  actualError: boolean;
  setDebugPNL: (value: number) => void;
  setDebugError: (value: boolean) => void;
}

export const useProgressDebug = (
  currentUserProgress: number,
  hasError: boolean,
): UseProgressDebugReturn => {
  // Debug state for PNL slider and error state
  const [debugPNL, setDebugPNL] = useState<number>(currentUserProgress);
  const [debugError, setDebugError] = useState<boolean>(hasError);
  // Track if user has manually changed the debug values
  const [hasManuallyChangedPNL, setHasManuallyChangedPNL] =
    useState<boolean>(false);
  const [hasManuallyChangedError, setHasManuallyChangedError] =
    useState<boolean>(false);

  // Update debug state when currentUserProgress changes (e.g., when navigating between accounts)
  useEffect(() => {
    setDebugPNL(currentUserProgress);
    setHasManuallyChangedPNL(false); // Reset manual change flag
  }, [currentUserProgress]);

  // Update debug state when hasError changes
  useEffect(() => {
    setDebugError(hasError);
    setHasManuallyChangedError(false); // Reset manual change flag
  }, [hasError]);

  // Use debug values if user has manually changed them, otherwise use props
  const actualPNL = hasManuallyChangedPNL ? debugPNL : currentUserProgress;
  const actualError = hasManuallyChangedError ? debugError : hasError;

  // Enhanced setters that track manual changes
  const setDebugPNLWithFlag = (value: number) => {
    setDebugPNL(value);
    setHasManuallyChangedPNL(true);
  };

  const setDebugErrorWithFlag = (value: boolean) => {
    setDebugError(value);
    setHasManuallyChangedError(true);
  };

  return {
    debugPNL,
    debugError,
    actualPNL,
    actualError,
    setDebugPNL: setDebugPNLWithFlag,
    setDebugError: setDebugErrorWithFlag,
  };
};
